import apiClient from '@/services/api'
import { deepCopy } from '@/utils/deep-copy'

const sortMessagesChronologically = messages =>
  messages
    .map((message, index) => ({
      message,
      index,
      timestamp: Date.parse(message.created_at)
    }))
    .sort((first, second) => first.timestamp - second.timestamp || first.index - second.index)
    .map(({ message }) => message)

export default {
  setChat({ commit }, { chat }) {
    commit('SET_CURRENT_CHAT', chat)
  },
  setChats({ commit }, { chats }) {
    commit('SET_CHATS', chats)
  },
  setOverload({ commit }, { chat }) {
    commit('SET_OVERLOAD_MESSAGES', chat)
  },

  async getChats({ rootState, commit, state }, { page = 1, size = 10, query }) {
    try {
      const { data } = await apiClient.post('/api/chat/search', {
        page,
        size,
        project_id: rootState.applicationState.currentProject.id,
        only_special: false,
        only_personal: true,
        associate_number: rootState.applicationState.associate.associate_number,
        query
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading chats'}`)
      }

      const existingIds = new Set(state.chats.map(chat => chat.id))
      const newChats = data.items.filter(chat => !existingIds.has(chat.id))

      commit('SET_TOTAL_CHATS_LENGTH', data.total_count)
      commit('SET_CHATS', [...state.chats, ...newChats])
    } catch (error) {
      throw error.message
    }
  },

  async getChatMessages({ rootState, commit, state }, { chat_id, size, offset }) {
    try {
      const { data } = await apiClient.post('/api/chat/message/get', {
        chat_id,
        descending: true,
        size,
        offset,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading chat'}`)
      }

      const updatedChat =
        state.currentChat && state.currentChat.id === chat_id
          ? state.currentChat
          : state.chats.find(item => item.id === chat_id)

      if (!updatedChat) return

      const mergedMessages = []

      const existingIds = new Set()

      if (updatedChat.messages) {
        updatedChat.messages.forEach(msg => {
          if (msg.id) existingIds.add(msg.id)
        })
      }

      data.messages.reverse().forEach(msg => {
        if (msg.id && !existingIds.has(msg.id)) {
          mergedMessages.push(msg)
          existingIds.add(msg.id)
        }
      })

      updatedChat.messages = sortMessagesChronologically(
        updatedChat.messages ? [...mergedMessages, ...updatedChat.messages] : mergedMessages
      )

      if (data.messages.length < size && state.currentChat?.id === chat_id) {
        commit('SET_OVERLOAD_MESSAGES', true)
      }

      commit(
        'SET_CHATS',
        state.chats.map(item => {
          if (item.id === chat_id) {
            return { ...item, messages: updatedChat.messages }
          } else {
            return { ...item }
          }
        })
      )
      if (state.currentChat?.id === chat_id) {
        commit('SET_CURRENT_CHAT', deepCopy(updatedChat))
      }
    } catch (error) {
      throw error.message
    }
  },

  async getChatMedia(_, { url }) {
    try {
      const { data } = await apiClient.get(`/api/marketplace/proxy?url=${url}`, {
        responseType: 'arraybuffer'
      })

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postChat({ rootState, commit, state }, { name, associates }) {
    try {
      const { data } = await apiClient.post('/api/chat/create', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number: rootState.applicationState.associate.associate_number,
        name,
        associates: [
          ...associates.filter(item => item !== rootState.applicationState.associate.associate_number),
          rootState.applicationState.associate.associate_number
        ]
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while creating chat'}`)
      }

      if (!data.chat.users.length) {
        throw new Error('Error while creating chat')
      }

      commit('SET_CHATS', [data.chat, ...state.chats])

      return data.chat
    } catch (error) {
      throw error.message
    }
  },

  async postChatLogo({ rootState, state }, { chat_id, logo }) {
    const formData = new FormData()

    formData.append('project_id', rootState.applicationState.currentProject.id)
    formData.append('associate_number', rootState.applicationState.associate.associate_number)
    formData.append('chat_id', chat_id)
    formData.append('logo ', logo)

    try {
      const { data } = await apiClient.post('/api/chat/logo', formData)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async deleteChat({ rootState, commit, state }, { chat_id }) {
    try {
      const { data } = await apiClient.post('/api/chat/delete', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number: rootState.applicationState.associate.associate_number,
        chat_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while deleting chat'}`)
      }

      commit(
        'SET_CHATS',
        state.chats.filter(item => item.id !== chat_id)
      )
      commit('SET_TOTAL_CHATS_LENGTH', state.total_chats_length - 1)
    } catch (error) {
      throw error.message
    }
  },

  async updateChat({ rootState, commit, state }, { chat_id, last_update }) {
    try {
      const { data } = await apiClient.post('/api/chat/message/update', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number: rootState.applicationState.associate.associate_number,
        chat_id,
        last_update
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading chats'}`)
      }

      const updatedChat =
        state.currentChat && state.currentChat.id === chat_id
          ? state.currentChat
          : state.chats.find(item => item.id === chat_id)

      if (!updatedChat) return

      const mergedMessages = []

      const existingIds = new Set()

      if (updatedChat.messages) {
        updatedChat.messages.forEach(msg => {
          if (msg.id) existingIds.add(msg.id)
        })
      }

      data.messages.reverse().forEach(msg => {
        if (msg.id && !existingIds.has(msg.id)) {
          mergedMessages.push(msg)
          existingIds.add(msg.id)
        }
      })

      updatedChat.messages = sortMessagesChronologically(
        updatedChat.messages ? [...updatedChat.messages, ...mergedMessages] : mergedMessages
      )

      commit(
        'SET_CHATS',
        state.chats.map(item => {
          if (item.id === chat_id) {
            return {
              ...item,
              messages: updatedChat.messages,
              last_message: updatedChat.messages[updatedChat.messages.length - 1]
            }
          } else {
            return { ...item }
          }
        })
      )
      if (state.currentChat?.id === chat_id) {
        commit('SET_CURRENT_CHAT', deepCopy(updatedChat))
      }
    } catch (error) {
      throw error.message
    }
  },

  async readChat({ rootState }, { chat_id }) {
    try {
      const { data } = await apiClient.post('/api/chat/message/read', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number: rootState.applicationState.associate.associate_number,
        chat_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading chats'}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async postMessage({ rootState, state, commit }, { chat_id, type, reply_to_id, text, attachments }) {
    const formData = new FormData()

    formData.append('project_id', rootState.applicationState.currentProject.id)
    formData.append('associate_number', rootState.applicationState.associate.associate_number)
    formData.append('chat_id', chat_id)
    formData.append('type', type)

    if (reply_to_id) formData.append('reply_to_id', reply_to_id)
    if (text) formData.append('text', text)

    if (attachments && attachments.length > 0) {
      for (let i = 0; i < attachments.length; i++) {
        formData.append('attachments', attachments[i])
      }
    }

    try {
      const { data } = await apiClient.post('/api/chat/message/send', formData)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading chats'}`)
      }

      const updatedChat =
        state.currentChat && state.currentChat.id === chat_id
          ? state.currentChat
          : state.chats.find(item => item.id === chat_id)

      if (!updatedChat) return data.message

      const messages = updatedChat.messages ?? []
      updatedChat.messages = sortMessagesChronologically(
        messages.some(message => message.id === data.message.id) ? messages : [...messages, data.message]
      )

      commit(
        'SET_CHATS',
        state.chats.map(item => {
          if (item.id === chat_id) {
            return {
              ...item,
              messages: updatedChat.messages,
              last_message: data.message
            }
          } else {
            return { ...item }
          }
        })
      )

      if (state.currentChat?.id === chat_id) {
        commit('SET_CURRENT_CHAT', deepCopy(updatedChat))
      }

      return data.message
    } catch (error) {
      throw error.message
    }
  }
}
